/**
 * Routes the phone app adds (src/aithora/v3/mobile.ts), per the mobile console handoff
 * (2026-10-02 and 2026-10-03). Session-scoped: each one acts for the signed-in person only.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

// ── Text size (Resident and Aithora Home; saved per person) ─────────────────

export type TextSizeLevel = 'normal' | 'large' | 'larger';

export interface TextSizeState {
  real: true;
  /** null until the person has chosen: then the phone's own setting is the default. */
  level: TextSizeLevel | null;
  updatedAt: string | null;
}

/** GET /me/text-size: the caller's saved choice, or null. */
export const textSizeGet = route<TextSizeState>()('GET', `${A}/me/text-size`, { honest: true });
/** PUT /me/text-size { level }: saved per person, so it follows them to every device. */
export const textSizeSet = route<TextSizeState, void, { level: TextSizeLevel }>()('PUT', `${A}/me/text-size`, { honest: true });

// ── Approval step-up (approve with your PIN or the phone's biometrics) ──────

export type ApprovalStepUpMethod = 'pin' | 'biometric';

export interface ApprovalStepUpResult {
  real: true;
  ok: boolean;
  /** Why it was refused: no PIN set yet, the PIN is locked, or a wrong PIN. */
  reason?: 'not_set' | 'locked' | 'wrong' | 'missing';
  /** Wrong tries left before the PIN locks. */
  left?: number;
  ledgerEntryId: string | null;
}

/**
 * POST /me/approval-step-up { method, pin?, item: { kind, id } }: before a decision is sent,
 * the person proves it is them. 'pin' is checked here against their Aithora PIN (wrong tries
 * count and lock it); 'biometric' is the phone's own Face ID or fingerprint, checked on the
 * phone. Either way the step-up is sealed to the Truth Ledger with the item it was for.
 */
export const approvalStepUp = route<ApprovalStepUpResult, void, { method: ApprovalStepUpMethod; pin?: string; item: { kind: string; id: string } }>()(
  'POST', `${A}/me/approval-step-up`, { honest: true, acceptStatuses: [403, 409, 423] },
);

// ── The emergency ladder, with who each step pages ──────────────────────────

export interface LadderStepView {
  n: number;
  /** Who this step pages, in a few words: a name, or "Everyone on staff". */
  who: string;
  /** How many people the step pages. */
  people: number;
  state: 'done' | 'current' | 'next';
}

export interface IncidentLadder {
  real: true;
  incidentId: string;
  plan: 'CRITICAL' | 'HIGH' | 'MEDIUM' | null;
  steps: LadderStepView[];
  /** The step paging now (1-based). */
  current: number;
  /** When the next step pages (ISO), or null when it will not move (claimed, or the last step). */
  nextAt: string | null;
  /** The family contacts told about the incident (Family is told at the claim and the close). */
  familyTold: string[];
}

/** GET /emergency/incidents/:id/ladder: the incident's real paging plan, step by step. */
export const incidentLadder = route<IncidentLadder>()('GET', `${A}/emergency/incidents/:id/ladder`, { honest: true });

// ── Alerts that are not emergencies ─────────────────────────────────────────

export interface MobileAlert {
  id: string;
  /** Every alert row of this one thing (one per person it paged). */
  ids: string[];
  title: string | null;
  description: string | null;
  severity: string;
  createdAt: string | null;
  residentName: string | null;
  room: string | null;
  facilityName: string | null;
  organizationId: string;
  /** A resident's "Call a nurse": the care request it is about (Acknowledge and Done act on it). */
  requestId?: string | null;
  /** OPEN until someone acknowledges it, then ACKNOWLEDGED until Done; it stays listed until Done. */
  requestStatus?: 'OPEN' | 'ACKNOWLEDGED' | null;
  /** Who acknowledged the request. */
  requestAcknowledgedBy?: string | null;
}

/** GET /mobile/alerts: the building's open alerts for its staff; every building's for Aithora. */
export const mobileAlerts = route<{ real: true; scope: 'facility' | 'network'; items: MobileAlert[] }>()('GET', `${A}/mobile/alerts`, { honest: true });
/** POST /mobile/alerts/:id/acknowledge: clears that alert for everyone it paged; sealed to the ledger. */
export const mobileAlertAcknowledge = route<{ real: true; ok: boolean; acknowledged: number; ledgerEntryId: string | null }>()(
  'POST', `${A}/mobile/alerts/:id/acknowledge`, { honest: true });
/** POST /mobile/alerts/:id/acknowledge/undo: the 10-second Undo, by the person who acknowledged (AF-1611); 409 once it has run out. */
export const mobileAlertAcknowledgeUndo = route<{ real: true; ok: boolean; undone: number; ledgerEntryId: string | null }>()(
  'POST', `${A}/mobile/alerts/:id/acknowledge/undo`, { honest: true });

export const mobileApi = { textSizeGet, textSizeSet, approvalStepUp, incidentLadder, mobileAlerts, mobileAlertAcknowledge, mobileAlertAcknowledgeUndo } as const;
